import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { 
    Delete as DeleteIcon,
  } from '@mui/icons-material';
import AddPermissionModal from "./AddPermissionModal";
import AddUserModal from "./AddUserModal";


function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`role-tabpanel-${index}`}
      aria-labelledby={`role-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ViewRole = () => {
  const { id } = useParams();
  const [role, setRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [roleLoading, setRoleLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);

  const [openAddPermission, setOpenAddPermission] = useState(false);
  const [openAddUser, setOpenAddUser] = useState(false);

  const fetchRole = () => {
    axios
      .get(`/role/${id}`)
      .then((res) => setRole(res.data.role))
      .catch((err) => console.error("Error fetching role details:", err))
      .finally(() => setRoleLoading(false));
  };

  const fetchUsers = () => {
    axios
      .get(`/users/role/${id}`)
      .then((res) => {setUsers(res.data.users); })
      .catch((err) => console.error("Error fetching role users:", err))
      .finally(() => setUsersLoading(false));
  };

  const removeUsers = (userId) => {
    axios
      .put(`/users/${userId}/role`)
      .then(() => fetchUsers())
      .catch((err) => console.error("Error removing role users:", err));
  };

  useEffect(() => {
    fetchRole();
    fetchUsers();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleRemovePermission = (permissionId) => {
    axios
      .delete(`/role/${role.id}/permission/${permissionId}`)
      .then(() => fetchRole())
      .catch((err) => console.error("Error removing permission:", err));
  };

  if (roleLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Page Header */}
        <Grid item xs={12}>
          <Typography variant="h4" component="h1" gutterBottom>
            Role Details
          </Typography>
        </Grid>

        {/* Left: Role Information Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="div" gutterBottom>
                {role.role_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {role.role_description}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Permissions:</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {role.permissions && role.permissions.length > 0 ? (
                    role.permissions.map((perm) => (
                      <Chip
                        key={perm.id}
                        label={perm.perm_name}
                        color="primary"
                        onDelete={() => handleRemovePermission(perm.id)}
                      />
                    ))
                  ) : (
                    <Typography variant="body2">
                      No permissions assigned
                    </Typography>
                  )}
                  {/* Chip to add new permissions */}
                  <Chip
                    label="Add Permission"
                    icon={<AddIcon />}
                    clickable
                    variant="outlined"
                    onClick={() => setOpenAddPermission(true)}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Tabs with Additional Details */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Associated Users" />
            </Tabs>


            {/* Tab: Associated Users */}
            <TabPanel value={currentTab} index={0}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="h6">Users Associated with this Role</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddUser(true)}
                >
                  Add User
                </Button>
              </Box>
              {usersLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table aria-label="associated users table">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users && users.length > 0 ? (
                        users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.id}</TableCell>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.phoneNumber}</TableCell>
                                <TableCell>
                                    <Tooltip title="Delete Driver">
                                        <span>
                                            <DeleteIcon
                                                color="error"
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.2s',
                                                    '&:hover': {
                                                        transform: 'scale(1.2)',
                                                        color: '#ff0000'
                                                    }
                                                }}
                                                onClick={() => removeUsers(user.id)}
                                            />
                                        </span>
                                    </Tooltip>
                                </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No users associated with this role.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Modals for adding permissions and users */}
      <AddPermissionModal
        open={openAddPermission}
        onClose={() => setOpenAddPermission(false)}
        roleId={role.id}
        currentPermissions={role.permissions || []}
        onAdded={fetchRole}
      />
      <AddUserModal
        open={openAddUser}
        onClose={() => setOpenAddUser(false)}
        roleId={role.id}
        currentUsers={users || []}
        onAdded={fetchUsers}
      />
    </Container>
  );
};

export default ViewRole;
